import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Send, Settings, Trash2, Loader2, Sparkles, ArrowLeft, Info, Check, RefreshCw, Play, Edit3, MessageSquare, Zap } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { SEO } from '../components/SEO';
interface ChatMessage {
  role: 'user' | 'bot';
  content: string;
  timestamp: string;
}
export function TryDemoPage() {
  const {
    language,
    t
  } = useLanguage();
  const isRTL = language === 'ar';
  // State
  const [systemMessage, setSystemMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChatEnabled, setIsChatEnabled] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Load from sessionStorage on mount
  useEffect(() => {
    const storedSysMsg = sessionStorage.getItem('operines_demo_system_message');
    const storedHistory = sessionStorage.getItem('operines_demo_chat_history');
    if (storedSysMsg) {
      setSystemMessage(storedSysMsg);
      setIsChatEnabled(true);
    }
    if (storedHistory) {
      try {
        setChatHistory(JSON.parse(storedHistory));
      } catch (e) {
        console.error('Failed to parse chat history', e);
      }
    }
  }, []);
  // Auto-scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [chatHistory, isLoading]);
  const handleApplySystemMessage = () => {
    if (!systemMessage.trim()) return;
    sessionStorage.setItem('operines_demo_system_message', systemMessage);
    setIsChatEnabled(true);
    // Add initial system greeting if history is empty
    if (chatHistory.length === 0) {
      const initialMsg: ChatMessage = {
        role: 'bot',
        content: isRTL ? 'مرحباً! لقد تم إعدادي بناءً على تعليماتك. كيف يمكنني مساعدتك؟' : 'Hello! I have been configured based on your instructions. How can I help you?',
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      setChatHistory([initialMsg]);
      sessionStorage.setItem('operines_demo_chat_history', JSON.stringify([initialMsg]));
    }
  };
  const handleReset = () => {
    sessionStorage.removeItem('operines_demo_system_message');
    sessionStorage.removeItem('operines_demo_chat_history');
    setSystemMessage('');
    setChatHistory([]);
    setIsChatEnabled(false);
  };
  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isLoading) return;
    const userMsg: ChatMessage = {
      role: 'user',
      content: currentMessage.trim(),
      timestamp: new Date().toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
    const updatedHistory = [...chatHistory, userMsg];
    setChatHistory(updatedHistory);
    sessionStorage.setItem('operines_demo_chat_history', JSON.stringify(updatedHistory));
    setCurrentMessage('');
    setIsLoading(true);
    try {
      // CRITICAL: Send systemMessage to webhook
      const response = await fetch('https://n8n.operines.me/webhook/chatgpt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'demo_chat',
          message: userMsg.content,
          systemMessage: systemMessage,
          language,
          isDemo: true,
          timestamp: new Date().toISOString(),
          chatHistory: chatHistory.map(m => ({
            role: m.role,
            content: m.content
          })) // Send history for context
        })
      });
      let replyText = '';
      if (response.ok) {
        const rawData = await response.json();
        // Handle both array (n8n "All Incoming Items") and object responses
        const data = Array.isArray(rawData) ? rawData[0] : rawData;
        replyText = data?.output || data?.reply || data?.message || data?.content || data?.text || '';
        // If still empty, try to get any string value from the response
        if (!replyText && data && typeof data === 'object') {
          const values = Object.values(data);
          const firstString = values.find(v => typeof v === 'string' && (v as string).length > 5);
          if (firstString) replyText = firstString as string;
        }
      }
      if (!replyText) {
        replyText = isRTL ? 'عذراً، حدث خطأ في الاتصال. يرجى المحاولة مرة أخرى.' : 'Sorry, connection error. Please try again.';
      }
      const botMsg: ChatMessage = {
        role: 'bot',
        content: replyText,
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      const finalHistory = [...updatedHistory, botMsg];
      setChatHistory(finalHistory);
      sessionStorage.setItem('operines_demo_chat_history', JSON.stringify(finalHistory));
    } catch (error) {
      console.error('Demo chat error:', error);
      const errorMsg: ChatMessage = {
        role: 'bot',
        content: isRTL ? 'عذراً، حدث خطأ غير متوقع.' : 'Sorry, an unexpected error occurred.',
        timestamp: new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      setChatHistory([...updatedHistory, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };
  const examplePrompts = [{
    title: isRTL ? 'خدمة عملاء مطعم' : 'Restaurant Customer Service',
    prompt: isRTL ? 'أنت موظف خدمة عملاء لمطعم بيتزا إيطالي اسمه "نابولي". المنيو يحتوي على بيتزا مارجريتا (150ج)، بيبروني (180ج)، وسي فود (220ج). التوصيل مجاني للطلبات فوق 500ج. كن ودوداً ومختصراً.' : 'You are a customer service agent for an Italian pizza restaurant called "Napoli". Menu: Margherita ($10), Pepperoni ($12), Seafood ($15). Free delivery for orders over $30. Be friendly and concise.'
  }, {
    title: isRTL ? 'حجز عيادة أسنان' : 'Dental Clinic Booking',
    prompt: isRTL ? 'أنت مساعد لحجز المواعيد في عيادة "ابتسامة". المواعيد المتاحة من 10 صباحاً لـ 6 مساءً. سعر الكشف 300ج. العنوان: المعادي، شارع 9. اسأل العميل عن اسمه ورقم هاتفه لتأكيد الحجز.' : 'You are a booking assistant for "Smile" Dental Clinic. Available hours: 10 AM to 6 PM. Consultation fee: $50. Address: 123 Main St. Ask for customer name and phone to confirm booking.'
  }];
  return <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-3255">
      <SEO title={isRTL ? 'جرب المساعد الذكي - Operines' : 'Try AI Assistant - Operines'} description={isRTL ? 'جرب قدرات الذكاء الاصطناعي في الرد على العملاء. قم بإعداد رسالة النظام وابدأ المحادثة فوراً.' : 'Experience AI customer support capabilities. Configure the system message and start chatting instantly.'} canonicalPath="/try-demo" language={language as 'ar' | 'en'} data-id="element-3256" />
      <Navbar data-id="element-3257" />

      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-3258">
        {/* Header Section */}
        <div className="text-center mb-10" data-id="element-3259">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-[#4f1964] text-sm font-medium mb-4" data-id="element-3260">
            <Sparkles className="w-4 h-4" data-id="element-3261" />
            {isRTL ? 'تجربة تفاعلية حية' : 'Live Interactive Demo'}
          </motion.div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-3262">
            {isRTL ? 'قم بتدريب مساعدك الذكي في ثوانٍ' : 'Train Your AI Assistant in Seconds'}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-3263">
            {isRTL ? 'اكتب تعليمات للمساعد الذكي في اليسار، ثم جرب المحادثة معه في اليمين كأنك عميل على واتساب.' : 'Write instructions for the AI on the left, then chat with it on the right as if you were a customer on WhatsApp.'}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start" data-id="element-3264">
          {/* LEFT SIDE: Configuration */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="lg:col-span-5 space-y-6" data-id="element-3265">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-3266">
              <div className="p-6 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between" data-id="element-3267">
                <div className="flex items-center gap-2 text-[#4f1964]" data-id="element-3268">
                  <Settings className="w-5 h-5" data-id="element-3269" />
                  <h2 className="font-bold text-lg" data-id="element-3270">
                    {isRTL ? 'إعدادات المساعد' : 'Assistant Config'}
                  </h2>
                </div>
                {isChatEnabled && <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium" data-id="element-3271">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-3272" />
                    {isRTL ? 'نشط' : 'Active'}
                  </span>}
              </div>

              <div className="p-6" data-id="element-3273">
                <div className="mb-4" data-id="element-3274">
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between" data-id="element-3275">
                    <span data-id="element-3276">
                      {isRTL ? 'تعليمات البوت (System Message)' : 'Bot Instructions (System Message)'}
                    </span>
                    {isChatEnabled && <span className="text-xs text-gray-500 font-normal" data-id="element-3277">
                        {isRTL ? 'يمكنك التعديل في أي وقت' : 'You can edit anytime'}
                      </span>}
                  </label>
                  <div className="relative" data-id="element-3278">
                    <textarea value={systemMessage} onChange={e => setSystemMessage(e.target.value)} placeholder={isRTL ? 'مثال: أنت مساعد خدمة عملاء لشركة شحن. الأسعار كالتالي...' : 'Example: You are a customer service agent for a shipping company. Prices are...'} className={`w-full h-52 p-4 border rounded-xl focus:ring-2 focus:ring-[#4f1964] focus:border-transparent resize-none text-sm leading-relaxed transition-all ${isChatEnabled ? 'bg-gray-50 border-green-200 text-gray-800' : 'bg-white border-gray-300 text-gray-900'}`} dir={isRTL ? 'rtl' : 'ltr'} data-id="element-3279" />
                    {isChatEnabled && <div className="absolute top-3 right-3" data-id="element-3280">
                        <Edit3 className="w-4 h-4 text-gray-400" data-id="element-3281" />
                      </div>}
                  </div>
                  <p className="text-xs text-gray-500 mt-2 flex items-start gap-1.5 leading-tight" data-id="element-3282">
                    <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" data-id="element-3283" />
                    {isRTL ? 'هذه التعليمات هي "عقل" البوت. كلما كانت أكثر تفصيلاً، كانت الردود أفضل.' : 'These instructions are the bot\'s "brain". The more detailed they are, the better the responses.'}
                  </p>
                </div>

                <div className="flex gap-3 pt-2" data-id="element-3284">
                  <Button fullWidth onClick={handleApplySystemMessage} disabled={!systemMessage.trim()} className={`flex-1 ${isChatEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-[#4f1964] hover:bg-[#3d1350]'} text-white transition-colors`} data-id="element-3285">
                    {isChatEnabled ? <>
                        <RefreshCw className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-3286" />
                        {isRTL ? 'تحديث التعليمات' : 'Update Instructions'}
                      </> : <>
                        <Play className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-3287" />
                        {isRTL ? 'تفعيل وبدء التجربة' : 'Apply & Start Demo'}
                      </>}
                  </Button>

                  {isChatEnabled && <Button variant="outline" onClick={handleReset} className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 px-3" title={isRTL ? 'إعادة تعيين' : 'Reset'} data-id="element-3288">
                      <Trash2 className="w-4 h-4" data-id="element-3289" />
                    </Button>}
                </div>
              </div>
            </div>

            {/* Examples */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-id="element-3290">
              <h3 className="font-bold text-gray-900 mb-4 text-xs uppercase tracking-wider text-gray-500" data-id="element-3291">
                {isRTL ? 'أمثلة جاهزة للنسخ' : 'Quick Templates'}
              </h3>
              <div className="space-y-3" data-id="element-3292">
                {examplePrompts.map((ex, i) => <button key={i} onClick={() => {
                setSystemMessage(ex.prompt);
                // Optional: auto-apply
                // handleApplySystemMessage()
              }} className="w-full text-start p-3 rounded-xl border border-gray-200 hover:border-[#4f1964] hover:bg-purple-50 transition-all group" data-id="element-3293">
                    <div className="flex items-center justify-between mb-1" data-id="element-3294">
                      <div className="font-bold text-gray-800 text-sm" data-id="element-3295">
                        {ex.title}
                      </div>
                      <div className="text-[#4f1964] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3296">
                        <Zap className="w-3 h-3" data-id="element-3297" />
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 line-clamp-2 leading-relaxed" data-id="element-3298">
                      {ex.prompt}
                    </div>
                  </button>)}
              </div>
            </div>
          </motion.div>

          {/* RIGHT SIDE: WhatsApp Chat */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="lg:col-span-7" data-id="element-3299">
            <div className="bg-[#E5DDD5] rounded-2xl shadow-xl overflow-hidden border border-gray-200 flex flex-col h-[600px] relative" data-id="element-3300">
              {/* WhatsApp Header */}
              <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3 shadow-md z-10" data-id="element-3301">
                <div className="flex items-center gap-3 flex-1" data-id="element-3302">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden border-2 border-white/20" data-id="element-3303">
                    <img src="https://cdn.magicpatterns.com/uploads/faWT17kE43WdqrZMBmZAS3/Operines-Icon-color1_(2).png" alt="Bot" className="w-7 h-7 object-contain" data-id="element-3304" />
                  </div>
                  <div data-id="element-3305">
                    <h3 className="text-white font-semibold text-base leading-tight" data-id="element-3306">
                      Operines AI
                    </h3>
                    <p className="text-white/80 text-xs flex items-center gap-1" data-id="element-3307">
                      <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" data-id="element-3308" />
                      {isRTL ? 'متصل الآن' : 'Online'}
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 text-white/80" data-id="element-3309">
                  <Settings className="w-5 h-5 cursor-pointer hover:text-white" data-id="element-3310" />
                </div>
              </div>

              {/* Chat Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 relative" style={{
              backgroundImage: 'url("https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png")',
              backgroundRepeat: 'repeat',
              backgroundSize: '400px',
              backgroundColor: '#E5DDD5'
            }} data-id="element-3311">
                {!isChatEnabled && <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-20 flex items-center justify-center p-6" data-id="element-3312">
                    <motion.div initial={{
                  scale: 0.9,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm text-center" data-id="element-3313">
                      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 text-[#4f1964]" data-id="element-3314">
                        <Bot className="w-8 h-8" data-id="element-3315" />
                      </div>
                      <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-3316">
                        {isRTL ? 'المساعد جاهز!' : 'Assistant Ready!'}
                      </h3>
                      <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-3317">
                        {isRTL ? 'قم بكتابة تعليمات البوت في القائمة الجانبية واضغط "تفعيل" لبدء المحادثة.' : 'Write the bot instructions in the sidebar and click "Apply" to start chatting.'}
                      </p>
                      <div className="flex items-center justify-center gap-2 text-sm text-purple-600 font-medium animate-pulse" data-id="element-3318">
                        <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} data-id="element-3319" />
                        {isRTL ? 'ابدأ من هنا' : 'Start here'}
                      </div>
                    </motion.div>
                  </div>}

                {chatHistory.map((msg, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-3320">
                    <div className={`max-w-[85%] rounded-lg px-3 py-1.5 shadow-sm relative text-[15px] leading-relaxed ${msg.role === 'user' ? 'bg-[#DCF8C6] text-gray-900 rounded-tr-none' : 'bg-white text-gray-900 rounded-tl-none'}`} data-id="element-3321">
                      {/* Tail */}
                      <div className={`absolute top-0 w-0 h-0 border-[8px] border-transparent ${msg.role === 'user' ? 'right-[-8px] border-t-[#DCF8C6] border-l-[#DCF8C6]' : 'left-[-8px] border-t-white border-r-white'}`} data-id="element-3322" />

                      <p className="whitespace-pre-wrap" data-id="element-3323">{msg.content}</p>
                      <div className={`text-[11px] text-right mt-1 flex items-center justify-end gap-1 ${msg.role === 'user' ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-3324">
                        {msg.timestamp}
                        {msg.role === 'user' && <span className="text-blue-500" data-id="element-3325">
                            <Check className="w-3.5 h-3.5" data-id="element-3326" />
                          </span>}
                      </div>
                    </div>
                  </motion.div>)}

                {isLoading && <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="flex justify-start" data-id="element-3327">
                    <div className="bg-white rounded-lg rounded-tl-none px-4 py-3 shadow-sm relative" data-id="element-3328">
                      <div className="absolute top-0 left-[-8px] w-0 h-0 border-[8px] border-transparent border-t-white border-r-white" data-id="element-3329" />
                      <div className="flex gap-1.5" data-id="element-3330">
                        <motion.div animate={{
                      scale: [1, 1.2, 1]
                    }} transition={{
                      repeat: Infinity,
                      duration: 0.6
                    }} className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-3331" />
                        <motion.div animate={{
                      scale: [1, 1.2, 1]
                    }} transition={{
                      repeat: Infinity,
                      duration: 0.6,
                      delay: 0.2
                    }} className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-3332" />
                        <motion.div animate={{
                      scale: [1, 1.2, 1]
                    }} transition={{
                      repeat: Infinity,
                      duration: 0.6,
                      delay: 0.4
                    }} className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-3333" />
                      </div>
                    </div>
                  </motion.div>}
                <div ref={messagesEndRef} data-id="element-3334" />
              </div>

              {/* Input Area */}
              <div className="bg-[#F0F0F0] p-3 flex items-center gap-2 z-10 border-t border-gray-200" data-id="element-3335">
                <input type="text" value={currentMessage} onChange={e => setCurrentMessage(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendMessage()} placeholder={isRTL ? 'اكتب رسالة...' : 'Type a message...'} className="flex-1 bg-white rounded-full px-5 py-3 text-base focus:outline-none border-none shadow-sm placeholder-gray-400" disabled={!isChatEnabled || isLoading} dir={isRTL ? 'rtl' : 'ltr'} data-id="element-3336" />
                <button onClick={handleSendMessage} disabled={!isChatEnabled || isLoading || !currentMessage.trim()} className="w-12 h-12 bg-[#008a7c] hover:bg-[#006f64] rounded-full flex items-center justify-center text-white shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95" data-id="element-3337">
                  {isLoading ? <Loader2 className="w-6 h-6 animate-spin" data-id="element-3338" /> : <Send className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} data-id="element-3339" />}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>;
}